/**
 * Claim an expense.
 *
 * The claim is always the signed-in person's — there is no "who", for the same
 * reason time entries have none: claiming on somebody's behalf is a different
 * act, and the API takes the employee from the session.
 *
 * **Rebillable** is the field that matters downstream. It says the client
 * agreed to carry this cost, and only a rebillable, approved claim can be
 * pulled onto an invoice. Ticking it does not put it on one.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  ApiError,
  Button,
  Input,
  Modal,
  RecordSelect,
  Select,
  Switch,
  api,
  toast,
  todayISO,
} from '@connect/web-shared';

const schema = z.object({
  incurred_on: z.string().min(1, 'When was this spent?'),
  category: z.enum(['travel', 'accommodation', 'meals', 'software', 'hardware', 'other']),
  description: z.string().trim().min(1, 'Say what it was for.').max(255),
  amount: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'An amount, to two decimal places.')
    .refine((value) => Number(value) > 0, 'More than nothing.'),
  currency: z.string().trim().length(3, 'Use the three-letter currency code.'),
  rebillable: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface Options {
  categories: { value: string; label: string }[];
}

// A FUNCTION, not a constant. A module-level object would capture `todayISO()`
// once, when the bundle loads — so a console left open overnight would go on
// offering yesterday's date, and the entry would be filed against the wrong
// day by somebody who never looked at the field.
const DEFAULTS = (): FormValues => ({
  incurred_on: todayISO(),
  category: 'travel',
  description: '',
  amount: '',
  currency: 'INR',
  rebillable: false,
});

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((code) => ({
  value: code,
  label: code,
}));

export function ExpenseClaimDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: (publicId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [project, setProject] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['timesheets', 'options'],
    queryFn: () => api.get<Options>('/timesheets/options'),
    staleTime: 10 * 60_000,
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS() });

  useEffect(() => {
    if (!open) return;
    form.reset(DEFAULTS());
    setProject(null);
  }, [open, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ public_id: string; code: string }>('/expenses', {
        ...values,
        project_id: project,
      }),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Claim submitted', record.code);
      onSaved?.(record.public_id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS()) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const rebillable = form.watch('rebillable');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New expense claim"
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() => void form.handleSubmit((values) => save.mutateAsync(values))()}
          >
            Submit claim
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => save.mutateAsync(values))();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <Input
          label="What was it?"
          required
          placeholder="Return flight — Mumbai to Pune"
          error={form.formState.errors.description?.message}
          {...form.register('description')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Date"
            type="date"
            required
            error={form.formState.errors.incurred_on?.message}
            {...form.register('incurred_on')}
          />
          <Select
            label="Category"
            options={options.data?.categories ?? []}
            error={form.formState.errors.category?.message}
            {...form.register('category')}
          />
          <Input
            label="Amount"
            required
            inputMode="decimal"
            placeholder="0.00"
            error={form.formState.errors.amount?.message}
            {...form.register('amount')}
          />
          <Select
            label="Currency"
            options={CURRENCIES}
            error={form.formState.errors.currency?.message}
            {...form.register('currency')}
          />
        </div>

        <RecordSelect
          label="Project"
          endpoint="/projects"
          value={project}
          onChange={(next) => setProject(next)}
          toOption={(row) => ({
            value: String(row.public_id),
            label: String(row.name),
            detail: String(row.client_name ?? ''),
          })}
          placeholder="Not against a project"
          hint={
            rebillable
              ? 'Needed to rebill it — the invoice has to know which client carries the cost.'
              : undefined
          }
        />

        <Switch
          label="Rebillable to the client"
          hint="Only an approved, rebillable claim can be pulled onto an invoice. Ticking this does not put it on one."
          {...form.register('rebillable')}
        />
      </form>
    </Modal>
  );
}
